import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { replace } from 'connected-react-router'
import { layouts, common } from '@ocean/ui'
import { SessionUtils, ToastUtils } from '@ocean/utils'
import { UserUtils } from 'utils'
import { handleUnauthorizedEvent } from 'utils/EventUtils'
import { useLazySelector } from 'hooks'
import Skeleton, { SkeletonVariant } from 'components/common/Skeleton'
import AssessmentPlayerRoutes from 'modules/AssessmentPlayer/routes'
import homeRoutes from 'modules/Home/routing/routes'
import { useAdaptiveLearningContext } from 'modules/requirements/Requirements/hooks/useAdaptiveLearningContext'
import { requirementsRoutes } from 'modules/requirements/Requirements/routing'
import {
  AdaptiveLearningEvent,
  AdaptiveLearningEvents,
} from 'modules/requirements/Requirements/types'
import libraryRoutes from 'modules/trainingLibrary/TrainingLibrary/routing/routes'

const { PageLayout, PageSegment } = layouts
const { Decorator } = common

const AdaptiveLearningContentPlayer: React.FC = () => {
  const [olpSourcePathname] = useState(() => {
    const pathname = sessionStorage.getItem('OLP_SOURCE_PATH')
    return pathname
  })
  const adaptiveLearningContentPlayerRef = useRef<HTMLIFrameElement>(null)
  const { isLoading, error, customerId } = useLazySelector(({ system }) => {
    const { isLoading, result, error } = system.customerInformation
    return {
      isLoading,
      customerId: result?.information.customerId,
      error,
    }
  })
  const dispatch = useDispatch()
  const adaptiveLearning = useAdaptiveLearningContext()

  useEffect(() => {
    if (!adaptiveLearning.isValid) {
      dispatch(replace(`${requirementsRoutes.root}`))
    }
  }, [adaptiveLearning.isValid, dispatch])

  useEffect(() => {
    const exitAdaptiveLearningContentPlayer = (e: MessageEvent<AdaptiveLearningEvent>) => {
      switch (e.data.type) {
        case AdaptiveLearningEvents.ExitContentPlayer: {
          dispatch(replace(`${requirementsRoutes.root}`, adaptiveLearning.buildContext(e.data)))
          break
        }

        case AdaptiveLearningEvents.AssessmentPlayer: {
          dispatch(replace(`${AssessmentPlayerRoutes.root}`, adaptiveLearning.buildContext(e.data)))
          break
        }

        case AdaptiveLearningEvents.ExitHome: {
          dispatch(replace(`${homeRoutes.root}`, adaptiveLearning.buildContext(e.data)))
          break
        }

        case AdaptiveLearningEvents.ExitLibrary: {
          dispatch(replace(`${libraryRoutes.root}`, adaptiveLearning.buildContext(e.data)))
          break
        }
      }
    }

    window.addEventListener('message', exitAdaptiveLearningContentPlayer)
    const cleanupUnauthorizedEvent = handleUnauthorizedEvent(dispatch)

    return () => {
      window.removeEventListener('message', exitAdaptiveLearningContentPlayer)
      cleanupUnauthorizedEvent()
    }
  }, [adaptiveLearning, dispatch])

  const sendTokenAndUserInfo = () => {
    if (adaptiveLearningContentPlayerRef.current) {
      const sessionToken = SessionUtils.getSessionToken()
      const { connectedPerson, login } = UserUtils.getUserAccountInfo()
      adaptiveLearningContentPlayerRef.current.contentWindow?.postMessage(
        {
          type: 'VIEW_ADAPTIVE_LEARNING_CONTENT',
          sessionToken,
          connectedPerson,
          username: login,
          customerId,
          olpSourcePathname: olpSourcePathname,
          ...adaptiveLearning.context,
        },
        window.cc.adaptiveLearningContentPlayer ?? ''
      )
      const parentDiv = adaptiveLearningContentPlayerRef.current.parentElement
      if (parentDiv) {
        parentDiv.style.overflow = 'hidden'
      }
    }
  }

  if (isLoading) {
    return <Skeleton variant={SkeletonVariant.Details} />
  }

  if (error) {
    ToastUtils.error(error.error)
    return null
  }

  if (!adaptiveLearning.isValid) {
    return null
  }

  return (
    <PageLayout>
      <PageSegment segmentId='content'>
        <Decorator root backgroundColor='white' textColor='black'>
          <iframe
            title='Adaptive Learning Content Player'
            ref={adaptiveLearningContentPlayerRef}
            src={`${window.cc.adaptiveLearningContentPlayer}${adaptiveLearning.queryString}`}
            onLoad={sendTokenAndUserInfo}
            width='100%'
            height='100%'
            style={{ border: 'none', overflow: 'auto' }}
            allow='fullscreen'
            role='application'
          />
        </Decorator>
      </PageSegment>
    </PageLayout>
  )
}

export default AdaptiveLearningContentPlayer
